/**
 * Previous-rental rows for the application-detail report.
 *
 * The rental step stores the most recent prior stay on the `previous*`
 * keys. Extra residences from the Minimum Living History popup land in
 * `livingHistory` with `source: 'additional'`. The Previous Rental card
 * shows both as one collapsible list (Figma 2026-09).
 */
import type { ParsedStepData } from './step-data-schemas'

export type PreviousResidenceRow = {
	id: string
	source: 'previous' | 'additional'
	livingType: string
	rangeLabel: string
	durationLabel: string
	rent: string
	landlordName: string
	landlordPhone: string
	landlordEmail: string
	paymentMethod: string
	address: string
	moveInIso: string
	moveOutIso: string
	showLandlordFields: boolean
	livingSituation: string
}

export type PreviousResidenceOpts = {
	/** Rental-step `previous*` stay (currently / previously renting only). */
	includeRentalStepPrevious: boolean
	/** Minimum Living History popup extras (`source: 'additional'`). */
	includePopupExtras: boolean
}

type LivingHistoryRow = NonNullable<
	NonNullable<ParsedStepData['rental']>['livingHistory']
>[number]

const SKIP_SOURCES = new Set([
	'current',
	'first-renter',
	'own-home',
	'other-room',
	'other-not-on-lease',
])

const MONTH_SHORT = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
] as const

/** Read year/month off the ISO prefix so UTC midnight does not slip a month. */
export function formatMonthYearRange(
	from: string | undefined,
	to: string | undefined,
): string {
	const start = yearMonthLabel(from)
	const end = yearMonthLabel(to)
	if (start && end) return `${start} - ${end}`
	return start || end || ''
}

export function buildPreviousResidenceRows(
	rental: ParsedStepData['rental'] | undefined,
	opts: PreviousResidenceOpts,
): PreviousResidenceRow[] {
	if (!rental) return []
	const rows: PreviousResidenceRow[] = []
	const seen = new Set<string>()

	if (opts.includeRentalStepPrevious) {
		const fromStep = fromRentalStepPrevious(rental)
		if (fromStep) {
			rows.push(fromStep)
			seen.add(fromStep.id)
		}
	}

	for (const entry of rental.livingHistory ?? []) {
		const source = entry.source?.trim() ?? 'additional'
		if (SKIP_SOURCES.has(source)) continue
		if (source === 'previous') {
			if (!opts.includeRentalStepPrevious) continue
			if (seen.has('lh_previous') || seen.has(entry.id ?? '')) continue
			rows.push(fromHistoryEntry(entry, 'previous'))
			seen.add(entry.id || 'lh_previous')
			continue
		}
		if (source !== 'additional' || !opts.includePopupExtras) continue
		const id = entry.id?.trim() || `lh_extra_${rows.length}`
		if (seen.has(id)) continue
		rows.push(fromHistoryEntry(entry, 'additional'))
		seen.add(id)
	}

	return rows
}

function fromRentalStepPrevious(
	rental: NonNullable<ParsedStepData['rental']>,
): PreviousResidenceRow | null {
	const address = rental.previousAddress?.trim() ?? ''
	const moveIn = rental.previousMoveInDate?.trim() ?? ''
	const moveOut = rental.previousMoveOutDate?.trim() ?? ''
	const landlordName = rental.previousLandlordName?.trim() ?? ''
	if (!address && !moveIn && !landlordName) return null
	return {
		id: 'lh_previous',
		source: 'previous',
		livingType: 'paying-rent',
		rangeLabel: formatMonthYearRange(moveIn, moveOut),
		durationLabel: rental.previousDurationLived?.trim() || '',
		rent: rental.previousRent?.trim() ?? '',
		landlordName,
		landlordPhone: rental.previousLandlordPhone?.trim() ?? '',
		landlordEmail: rental.previousLandlordEmail?.trim() ?? '',
		paymentMethod: rental.previousPaymentMethod?.trim() ?? '',
		address,
		moveInIso: moveIn,
		moveOutIso: moveOut,
		showLandlordFields: true,
		livingSituation: '',
	}
}

function fromHistoryEntry(
	entry: LivingHistoryRow,
	source: 'previous' | 'additional',
): PreviousResidenceRow {
	const livingType = entry.livingType?.trim() ?? ''
	const hasLandlord =
		Boolean(entry.landlordName?.trim()) ||
		Boolean(entry.rent?.trim()) ||
		livingType === 'paying-rent'
	return {
		id: entry.id?.trim() || `lh_${source}`,
		source,
		livingType,
		rangeLabel: formatMonthYearRange(entry.from, entry.to),
		durationLabel: entry.duration?.trim() || '',
		rent: entry.rent?.trim() ?? '',
		landlordName: entry.landlordName?.trim() ?? '',
		landlordPhone: entry.landlordNumber?.trim() ?? '',
		landlordEmail: entry.landlordEmail?.trim() ?? '',
		paymentMethod: entry.paymentMethod?.trim() ?? '',
		address: composeResidenceAddress(entry),
		moveInIso: entry.from?.trim() ?? '',
		moveOutIso: entry.to?.trim() ?? '',
		showLandlordFields: livingType !== 'not-paying-rent' && hasLandlord,
		livingSituation: entry.livingSituation?.trim() ?? '',
	}
}

function composeResidenceAddress(entry: LivingHistoryRow): string {
	const street = entry.address?.trim() ?? ''
	const city = entry.addressCity?.trim() ?? ''
	const province = entry.addressProvince?.trim() ?? ''
	const postal = entry.addressPostalCode?.trim() ?? ''
	if (street && (!city || street.includes(city))) return street
	const region = [province, postal].filter(Boolean).join(' ')
	return [street, city, region].filter(Boolean).join(', ')
}

function yearMonthLabel(iso: string | undefined): string {
	if (!iso?.trim()) return ''
	const match = iso.trim().match(/^(\d{4})-(\d{2})/)
	if (!match) return ''
	const month = Number(match[2])
	if (month < 1 || month > 12) return ''
	return `${MONTH_SHORT[month - 1]} ${match[1]}`
}
